/**
 * Prestige Theme - Global Scripts
 * Version: 1.0.0
 * Author: Aeria
 */

window.Theme = window.Theme || {};
window.Theme.utils = {
  /**
   * Simple debouncing function
   */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  /**
   * Fetch with error handling
   */
  async fetchWithRetry(url, options = {}, retries = 3) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (e) {
      if (retries > 0) return this.fetchWithRetry(url, options, retries - 1);
      throw e;
    }
  }
};

/**
 * Handle Drawers and Modals
 */
class Drawer {
  constructor(id) {
    this.drawer = document.getElementById(id);
    if (!this.drawer) return;
    this.isOpen = false;
    this.init();
  }

  init() {
    // Event delegation on the root drawer element to capture close actions,
    // ensuring dynamically replaced close buttons still work.
    this.drawer.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-close-drawer]');
      if (closeBtn) {
        this.close();
      }
    });
    
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Trap keyboard focus inside the drawer when open (A11y Compliance)
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.isOpen) return;
      
      const focusables = this.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex="0"]');
      if (focusables.length === 0) return;
      
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });
  }

  open() {
    this.drawer.setAttribute('open', '');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('overflow-hidden');
    this.isOpen = true;

    // Set initial focus to the first interactive element inside the drawer
    setTimeout(() => {
      const focusables = this.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex="0"]');
      if (focusables.length > 0) {
        focusables[0].focus();
      }
    }, 50);

    this.drawer.dispatchEvent(new CustomEvent('drawer:open'));
  }

  close() {
    this.drawer.removeAttribute('open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('overflow-hidden');
    this.isOpen = false;
    this.drawer.dispatchEvent(new CustomEvent('drawer:close'));
  }
}

window.Theme.Drawer = Drawer;

document.addEventListener('DOMContentLoaded', () => {
  console.log('Prestige Theme initialized');
});

function toggleCompatibleGear(event) {
  const block = document.querySelector('.cart-drawer__compatible_products');
  if (!block) return;
  
  if (event && event.detail && typeof event.detail.item_count !== 'undefined') {
    block.style.display = event.detail.item_count > 0 ? 'block' : 'none';
  } else {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        block.style.display = cart.item_count > 0 ? 'block' : 'none';
      })
      .catch(err => console.error('Error fetching cart for compatible gear:', err));
  }
}
document.addEventListener('DOMContentLoaded', toggleCompatibleGear);
document.addEventListener('cart:updated', toggleCompatibleGear);
document.addEventListener('cart:refresh', toggleCompatibleGear);

/**
 * Predictive Link Prefetching for Aeria Prestige (Instant Load Architecture)
 * Prefetches page documents on hover/touchstart after 80ms of delay to optimize perceived latency.
 */
class PredictivePrefetcher {
  constructor() {
    this.prefetchedUrls = new Set();
    this.hoverTimeout = null;
    this.init();
  }

  init() {
    const supportsPrefetch = () => {
      const link = document.createElement('link');
      return link.relList && link.relList.supports && link.relList.supports('prefetch');
    };
    this.canPrefetch = supportsPrefetch();

    // Event delegation for internal links
    document.addEventListener('mouseover', (e) => this.handleHover(e), { passive: true });
    document.addEventListener('touchstart', (e) => this.handleHover(e), { passive: true });
  }

  handleHover(e) {
    const link = e.target.closest('a');
    if (!link) return;

    const url = link.getAttribute('href');
    if (!url || !this.isPrefetchable(url)) return;

    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      this.prefetch(url);
    }, 80); // 80ms delay prevents prefetching links during fast scrolls
  }

  isPrefetchable(url) {
    if (url.startsWith('/admin') || url.startsWith('/cart') || url.startsWith('http') && !url.includes(window.location.hostname)) return false;
    if (url.includes('#') || url.includes('javascript:')) return false;
    if (this.prefetchedUrls.has(url)) return false;
    return true;
  }

  prefetch(url) {
    this.prefetchedUrls.add(url);
    if (this.canPrefetch) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    } else {
      // Fallback: low priority fetch
      fetch(url, { priority: 'low' }).catch(() => {});
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PredictivePrefetcher();
});

